import { FunctionComponent, SVGProps } from 'react'

import { generateId } from './util'
import { ReactComponent as RingCentralIcon } from '../assets/images/ringcentral-logo.svg'
import { ReactComponent as GotoIcon } from '../assets/images/goto-logo.svg'
import { ReactComponent as IntulseIcon } from '../assets/images/intulse-logo.svg'
import { ReactComponent as TeamsIcon } from '../assets/images/integration-logos/color/microsoft-teams-logo.svg'
import { ReactComponent as ZoomIcon } from '../assets/images/integration-logos/color/zoom-logo.svg'

import { SessionStorageData } from '../models/SessionStorage'
import { AccountIntegrationConfigMappingType } from '../models/Account'

export const createState = (redirectUri: string | null = null, step: string | null = null, customDomain: string | null = null): string => {
  const nonce = generateId()

  const data = JSON.stringify({
    redirectUri: redirectUri || window.location.origin + window.location.pathname,
    createdAt: new Date().getTime(),
    ...(step && { step }),
    ...(customDomain && { customDomain }),
  })

  window.sessionStorage.setItem(nonce, data)
  return nonce
}

const getStorageKeyAndDomain = (
  nonce: string,
  integrationServiceId: IntegrationServiceId | null,
): {
  storageKey: string
  customDomain?: string
} => {
  const parts = integrationServiceId === IntegrationServiceId.DYNAMICS || integrationServiceId === IntegrationServiceId.ENGAGE ? nonce.split('|') : []

  return {
    storageKey: parts.length === 2 ? parts[0] : nonce,
    customDomain: parts.length === 2 ? parts[1] : undefined,
  }
}

export const readState = (nonce: string, integrationServiceId: IntegrationServiceId | null = null): SessionStorageData | null => {
  const { storageKey, customDomain } = getStorageKeyAndDomain(nonce, integrationServiceId)

  const data = window.sessionStorage.getItem(storageKey)
  if (!data) {
    return null
  }

  const parsedData = JSON.parse(data) as SessionStorageData

  return {
    ...parsedData,
    customDomain: customDomain ?? parsedData.customDomain ?? null,
  }
}

export type CallSolution = {
  id: string
  displayName: string
  icon: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined }>
  getLink: (phoneNumber: string, code: string) => string
}
export const supportedCallSolutions: CallSolution[] = [
  {
    id: 'ringcentral',
    displayName: 'RingCentral',
    icon: RingCentralIcon,
    getLink: (phoneNumber: string, code: string) => `rcapp://r/call?number=${phoneNumber},,${code}`,
  },
  {
    id: 'intulse',
    displayName: 'Intulse',
    icon: IntulseIcon,
    getLink: (phoneNumber: string, code: string) => `tel:${phoneNumber},,${code}`,
  },
  {
    id: 'teams',
    displayName: 'Teams Phone',
    icon: TeamsIcon,
    getLink: (phoneNumber: string, code: string) => `https://teams.microsoft.com/l/call/0/0?users=4:${phoneNumber}#${code}`,
  },
  {
    id: 'zoom',
    displayName: 'Zoom Phone',
    icon: ZoomIcon,
    getLink: (phoneNumber: string, code: string) => `zoomphonecall://${phoneNumber},,${code}`,
  },
  {
    id: 'goto',
    displayName: 'GoTo Connect',
    icon: GotoIcon,
    getLink: (phoneNumber: string, code: string) => `tel:${phoneNumber},,${code}`,
  },
]

export const getMailIntegrationServiceLabel = (serviceId: string) => {
  return serviceId === IntegrationServiceId.OUTLOOK_MAIL ? 'Outlook' : serviceId.charAt(0).toUpperCase() + serviceId.slice(1)
}

export enum IntegrationServiceId {
  // Meeting
  PHONE = 'phone',
  ZOOM = 'zoom',
  TEAMS = 'teams',
  MEET = 'meet',
  WEBEX = 'webex',
  // Calendar
  GOOGLE_CALENDAR = 'googleCalendar',
  OUTLOOK_CALENDAR = 'outlookCalendar',
  EXCHANGE_CALENDAR = 'exchangeCalendar',
  WEALTHBOX_CALENDAR = 'wealthboxCalendar',
  // Email
  GMAIL = 'gmail',
  OUTLOOK_MAIL = 'outlookMail',
  // Financial Planning
  E_MONEY = 'emoney',
  RIGHT_CAPITAL = 'rightcapital',
  // Portfolio Management & Reporting
  ORION = 'orion',
  // Tax Planning
  HOLISTIPLAN = 'holistiplan',
  // CRM
  WEALTHBOX = 'wealthbox',
  SALESFORCE = 'salesforce',
  PRACTIFI = 'practifi',
  XLR8 = 'xlr8',
  ELEMENTS = 'elements',
  BLACK_DIAMOND = 'blackdiamond',
  REDTAIL = 'redtail',
  ZOHO = 'zoho',
  HUBSPOT = 'hubspot',
  DYNAMICS = 'dynamics',
  ENGAGE = 'engage',
  SMARTOFFICE = 'smartoffice',
  ADVISOR_ENGINE = 'advisorengine',
  INVESTCLOUD = 'investcloud',
  // Forms & Client Onboarding
  PRECISE_FP = 'precisefp',
  // Other
  CLIC = 'clic',
  CUSTOM = 'custom',
}

export enum IntegrationCategory {
  MEETING = 'meeting',
  CALENDAR = 'calendar',
  MAIL = 'mail',
  PLANNING = 'planning',
  PORTFOLIO = 'portfolio',
  TAX = 'tax',
  CRM = 'crm',
  ONBOARDING = 'onboarding',
  CLIC = 'clic',
}
const INTEGRATION_CATEGORY_MAP: Record<IntegrationCategory, IntegrationServiceId[]> = {
  [IntegrationCategory.CALENDAR]: [IntegrationServiceId.GOOGLE_CALENDAR, IntegrationServiceId.OUTLOOK_CALENDAR, IntegrationServiceId.EXCHANGE_CALENDAR],
  [IntegrationCategory.MAIL]: [IntegrationServiceId.GMAIL, IntegrationServiceId.OUTLOOK_MAIL],
  [IntegrationCategory.PLANNING]: [IntegrationServiceId.E_MONEY, IntegrationServiceId.RIGHT_CAPITAL],
  [IntegrationCategory.PORTFOLIO]: [IntegrationServiceId.ORION],
  [IntegrationCategory.TAX]: [IntegrationServiceId.HOLISTIPLAN],
  [IntegrationCategory.ONBOARDING]: [IntegrationServiceId.PRECISE_FP],
  [IntegrationCategory.MEETING]: [
    IntegrationServiceId.MEET,
    IntegrationServiceId.TEAMS,
    IntegrationServiceId.ZOOM,
    IntegrationServiceId.WEBEX,
    IntegrationServiceId.PHONE,
  ],
  [IntegrationCategory.CRM]: [
    IntegrationServiceId.WEALTHBOX,
    IntegrationServiceId.HUBSPOT,
    IntegrationServiceId.REDTAIL,
    IntegrationServiceId.SALESFORCE,
    IntegrationServiceId.PRACTIFI,
    IntegrationServiceId.XLR8,
    IntegrationServiceId.ZOHO,
    IntegrationServiceId.DYNAMICS,
    IntegrationServiceId.SMARTOFFICE,
    IntegrationServiceId.ELEMENTS,
    IntegrationServiceId.BLACK_DIAMOND,
    IntegrationServiceId.ENGAGE,
    IntegrationServiceId.ADVISOR_ENGINE,
    IntegrationServiceId.INVESTCLOUD,
  ],
  [IntegrationCategory.CLIC]: [IntegrationServiceId.CLIC],
}

export const INTEGRATION_CATEGORY_DISPLAY_NAME_MAP: { [key in IntegrationCategory]: string } = {
  [IntegrationCategory.MEETING]: 'Meeting',
  [IntegrationCategory.MAIL]: 'Email',
  [IntegrationCategory.CALENDAR]: 'Calendar',
  [IntegrationCategory.CRM]: 'CRM',
  [IntegrationCategory.PLANNING]: 'Financial Planning',
  [IntegrationCategory.ONBOARDING]: 'Forms & Client Onboarding',
  [IntegrationCategory.PORTFOLIO]: 'Portfolio Management',
  [IntegrationCategory.TAX]: 'Tax Planning',
  [IntegrationCategory.CLIC]: 'CLIC®',
}

export const INTEGRATION_DISPLAY_NAME_MAP: { [key in IntegrationServiceId]?: string } = {
  [IntegrationServiceId.E_MONEY]: 'eMoney',
  [IntegrationServiceId.RIGHT_CAPITAL]: 'RightCapital',
  [IntegrationServiceId.PRECISE_FP]: 'PreciseFP',
  [IntegrationServiceId.ORION]: 'Orion',
  [IntegrationServiceId.HOLISTIPLAN]: 'Holistiplan',
  [IntegrationServiceId.CLIC]: 'CLIC®',
  [IntegrationServiceId.ELEMENTS]: 'SS&C BlackDiamond Elements',
  [IntegrationServiceId.BLACK_DIAMOND]: 'SS&C BlackDiamond Edge',
  [IntegrationServiceId.ENGAGE]: 'SS&C BlackDiamond Engage',
  [IntegrationServiceId.INVESTCLOUD]: 'InvestCloud',
  [IntegrationServiceId.SMARTOFFICE]: 'SmartOffice',
  [IntegrationServiceId.ADVISOR_ENGINE]: 'AdvisorEngine',
  [IntegrationServiceId.GOOGLE_CALENDAR]: 'Google Calendar',
  [IntegrationServiceId.OUTLOOK_CALENDAR]: 'Outlook Calendar',
  [IntegrationServiceId.EXCHANGE_CALENDAR]: 'Exchange Calendar (On-Premise)',
  [IntegrationServiceId.MEET]: 'Google Meet',
  [IntegrationServiceId.TEAMS]: 'Microsoft Teams',
  [IntegrationServiceId.ZOOM]: 'Zoom',
  [IntegrationServiceId.OUTLOOK_MAIL]: 'Outlook Mail',
}

export const CONTACT_UPDATE_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.E_MONEY,
  IntegrationServiceId.PRECISE_FP,
  IntegrationServiceId.ORION,
  IntegrationServiceId.HOLISTIPLAN,
  IntegrationServiceId.RIGHT_CAPITAL,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  // IntegrationServiceId.ADVISOR_ENGINE,
  IntegrationServiceId.CLIC,
]

export const DOCUMENT_CONTACT_UPDATE_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.E_MONEY,
  IntegrationServiceId.PRECISE_FP,
  IntegrationServiceId.ORION,
  IntegrationServiceId.HOLISTIPLAN,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.CLIC,
]

export const MEETING_EXPORT_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.ADVISOR_ENGINE,
  IntegrationServiceId.INVESTCLOUD,
]

export const TASK_EXPORT_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.ADVISOR_ENGINE,
]

export const DOCUMENT_SUMMARY_EXPORT_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.ADVISOR_ENGINE,
  // IntegrationServiceId.INVESTCLOUD,
]

export const INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE = [
  IntegrationServiceId.PRECISE_FP,
  IntegrationServiceId.E_MONEY,
  IntegrationServiceId.CLIC,
  IntegrationServiceId.RIGHT_CAPITAL,
]

export const getIntegrationServiceIdsForCategory = (category: IntegrationCategory): IntegrationServiceId[] => {
  return INTEGRATION_CATEGORY_MAP[category] || []
}

export const getIntegrationCategoryDisplayNameByServiceId = (serviceId: string): string => {
  const integrationServiceId = serviceId as IntegrationServiceId

  const category = Object.entries(INTEGRATION_CATEGORY_MAP).find(([, serviceIds]) => serviceIds.includes(integrationServiceId))?.[0] as
    | IntegrationCategory
    | undefined

  return category ? getIntegrationCategoryDisplayName(category) : 'Unknown'
}

export const getIntegrationDisplayName = (serviceId: IntegrationServiceId): string => {
  return INTEGRATION_DISPLAY_NAME_MAP[serviceId] || serviceId.charAt(0).toUpperCase() + serviceId.slice(1)
}

export const getIntegrationCategoryDisplayName = (category: IntegrationCategory): string => {
  return INTEGRATION_CATEGORY_DISPLAY_NAME_MAP[category] || category.charAt(0).toUpperCase() + category.slice(1)
}

export const DEFAULT_ACCOUNT_INTEGRATION_CONFIG_MAPPINGS = {
  [AccountIntegrationConfigMappingType.CONTACT]: 'contact',
}

export const INTEGRATIONS_WITH_DYNAMIC_SCHEMA = [IntegrationServiceId.E_MONEY, IntegrationServiceId.PRECISE_FP, IntegrationServiceId.RIGHT_CAPITAL]

const DEFAULT_HIDDEN_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.CLIC, IntegrationServiceId.INVESTCLOUD]

export const isIntegrationDefaultHidden = (serviceId: IntegrationServiceId): boolean => {
  return DEFAULT_HIDDEN_INTEGRATIONS.includes(serviceId)
}

const UN_CONNECTABLE_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.TEAMS, IntegrationServiceId.MEET]

export const isIntegrationUnConnectable = (serviceId: IntegrationServiceId): boolean => {
  return UN_CONNECTABLE_INTEGRATIONS.includes(serviceId)
}
